import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Loader2, AlertTriangle } from "lucide-react";
import { APP_NAME } from "@/config";
import { useConfig } from "@/lib/flags";
import { Markdown, HEADING_CLASS } from "@/lib/markdown";
import PublicShell from "@/components/PublicShell";
// The public parts of docs/legal/, copied by `python backend/legal.py` (tests/test_legal.py checks them)
import termsUrl from "@/legal/terms-of-service.md";
import privacyUrl from "@/legal/privacy-policy.md";

const PAGES = {
  terms: { url: termsUrl, title: "Terms of Service" },
  privacy: { url: privacyUrl, title: "Privacy Policy" },
  // section "N. Refunds" of the terms, on its own page
  refunds: { url: termsUrl, title: "Refund policy", section: /^## \d+\. Refunds\b/ },
};

// The section of a document that starts with a heading matching `heading`, up to the next one of its level.
function section(markdown, heading) {
  const lines = markdown.split("\n");
  const start = lines.findIndex((l) => heading.test(l));
  if (start < 0) return null;
  const end = lines.findIndex((l, i) => i > start && /^##\s/.test(l));
  return lines.slice(start, end < 0 ? undefined : end).join("\n");
}

// /terms, /privacy and /refunds: public, no sign-in. Until LEGAL_PUBLISHED (GET /api/config) they say the
// texts are drafts.
export default function LegalPage({ page }) {
  const { url, title, section: heading } = PAGES[page];
  const [text, setText] = useState(null);
  const [failed, setFailed] = useState(false);
  const { hash } = useLocation();
  const published = useConfig().legal?.published === true;

  useEffect(() => {
    document.title = `${title} · ${APP_NAME}`;
    return () => { document.title = APP_NAME; };
  }, [title]);
  useEffect(() => {
    let live = true;
    setText(null);
    setFailed(false);
    fetch(url)
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(r.statusText))))
      .then((md) => {
        const shown = heading ? section(md, heading) : md;
        if (!live) return;
        if (shown == null) setFailed(true);
        else setText(shown);
      })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [url, heading]);
  useEffect(() => {  // a link to a section (/terms#11-refunds-once-paid-plans-open) opens on it
    if (text && hash) document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView();
  }, [text, hash]);

  return (
    <PublicShell testid={`legal-${page}`} className="text-base leading-relaxed text-gr-ink-secondary">
        {!published && (
          // Graphite: a status is a coloured word with its icon, on a flat surface
          <div role="note" className="mb-10 flex items-start gap-3 rounded-[10px] bg-card px-5 py-4 text-sm text-foreground"
            data-testid="legal-draft">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-gr-warning" aria-hidden="true" />
            <p><strong className="font-semibold text-gr-warning">Draft.</strong> This text is still being reviewed and may
            change before it takes effect.</p>
          </div>
        )}
        {page === "refunds" && (
          <>
            <h1 className={`${HEADING_CLASS[1]} text-foreground`}>{title}</h1>
            <p className="mb-2 text-muted-foreground">
              This is a section of our <Link to="/terms" className="underline underline-offset-2 hover:text-foreground">Terms of Service</Link>.
            </p>
          </>
        )}
        {failed ? <p className="text-muted-foreground">This page couldn't be loaded. Please try again later.</p>
          : text == null ? <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" aria-label="Loading" />
          : <Markdown text={text} />}
    </PublicShell>
  );
}
